import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";

import { Breadcrumbs } from "../AbstractElements";
import { titleCase, sentenceCase } from "../utils/case";
import { useTranslation } from "react-i18next";

import { getDashboard } from "../api/dashboard.api";

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await getDashboard();
      setData(res.data || res);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Live clock
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formattedDate = currentTime.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString();

  // Reservations → FullCalendar (ONLY confirmed)
  const reservationEvents =useMemo(() => {
    if (!data?.todayReservations) return [];
    console.log(data.todayReservations
      .filter((r) => r.status === "confirmed")
      .map((r) => ({
        title: `${r.venue.name}\n(${r.name})`,
        start: `${r.date}T${r.startTime}`,
        end: `${r.date}T${r.endTime}`,
        backgroundColor: "#7366ff",
        textColor: "#fff",
      })));

    return data.todayReservations
      .filter((r) => r.status === "confirmed")
      .map((r) => ({
        title: `${r.venue.name}\n(${r.name})`,
        start: `${r.date}T${r.startTime}`,
        end: `${r.date}T${r.endTime}`,
        backgroundColor: "#7366ff",
        textColor: "#fff",
      }));
  }, [data]);

  const earliestStartTime = useMemo(() => {
    if (!data?.todayReservations) return "06:00:00"; // fallback

    const earliest = data.todayReservations
      .filter((r) => r.status === "confirmed")
      .reduce((acc, r) => {
        if (!acc) return r;
        return r.startTime < acc.startTime ? r : acc;
      }, null);

    return earliest?.startTime || "06:00:00"; // fallback prevents FullCalendar issues
  }, [data]);

  // Device card (read-only)
  const renderDeviceCard = (device) => (
    <Col md="3" key={device.id}>
      <Card>
        <CardBody className={`d-flex flex-column justify-content-between rounded ${device.status === "on" ? "bg-success" : "bg-light"}`}>
          {/* Top: Name + Status */}
          <div className="d-flex justify-content-between align-items-center">
            <h6 className={`mb-0 ${device.status === "off" && "text-dark"}`}>{device.name}</h6>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("dashboard"))}
        title={titleCase(t("dashboard"))}
        mainTitle={titleCase(t("dashboard"))}
      />

      <Container fluid>
        {/* KPI ROW */}
        <Row>
          <Col md="7">
            <Row>
              {/* Date & Time */}
              <Col md="6" className="d-flex">
                <Card className="profile-box h-80 w-100">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <h6>{titleCase(t("today"))}</h6>
                    <h5>{formattedDate}</h5>
                    <h4 className="mt-2">{formattedTime}</h4>
                  </CardBody>
                </Card>
              </Col>

              {/* Total Reservations */}
              <Col md="6" className="d-flex">
                <Card className="h-80 w-100">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <h6>{titleCase(t("total reservations"))}</h6>
                    <h3>
                      {loading ? "..." : data?.totalReservations || 0}
                    </h3>
                  </CardBody>
                </Card>
              </Col>

              {/* Pending Reservations */}
              <Col md="6" className="d-flex">
                <Card className="h-80 w-100 bg-secondary">
                  <CardBody className="d-flex flex-column justify-content-center">
                    
                    {/* Header: Title (left) + Button (right) */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        {titleCase(t("pending reservations"))}
                      </h6>

                      <Button
                        color="outline-white"
                        size="sm"
                        onClick={() => navigate("/reservations?status=pending")}
                      >
                        {titleCase(t("go"))}
                      </Button>
                    </div>

                    {/* Value */}
                    <h3 className="mb-0">
                      {loading ? "..." : data?.pendingReservations || 0}
                    </h3>

                  </CardBody>
                </Card>
              </Col>

              <Col className="d-flex">
                <Card className="h-80 w-100 bg-secondary">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        {titleCase(t("pending transactions"))}
                      </h6>

                      <Button
                        color="outline-white"
                        size="sm"
                        onClick={() => navigate("/transactions?status=pending")}
                      >
                        {titleCase(t("go"))}
                      </Button>
                    </div>
                    <h3>
                      {loading ? "..." : data?.pendingTransactions || 0}
                    </h3>
                  </CardBody>
                </Card>
              </Col>

              {/* Finance ROW */}
              <Col md="4" className="d-flex">
                <Card className="h-80 w-100">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <h6>{titleCase(t("projected collections"))}</h6>
                    <h3>
                      RM {loading ? "..." : data?.projectedCollections?.toFixed(2) || 0}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md="4" className="d-flex">
                <Card className="h-80 w-100">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <h6>{titleCase(t("confirmed collections"))}</h6>
                    <h3>
                      RM {loading ? "..." : data?.confirmedCollections?.toFixed(2) || 0}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col md="4" className="d-flex">
                <Card className="h-80 w-100">
                  <CardBody className="d-flex flex-column justify-content-center">
                    <h6>{titleCase(t("receipts issued"))}</h6>
                    <h3>
                      {loading ? "..." : data?.totalReceiptsIssued || 0}
                    </h3>
                  </CardBody>
                </Card>
              </Col>
              <Col>
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">{titleCase(t("active devices"))}</h6>

                      <Button
                        color="outline-primary"
                        size="sm"
                        onClick={() => navigate("/devices")}
                      >
                        {titleCase(t("manage"))}
                      </Button>
                    </div>

                    <Row>
                      {data?.devices?.filter((d)=>{return d.status=="on"}).map((device) =>
                        renderDeviceCard(device)
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col md="5">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>{titleCase(t("today's reservations"))} ({data?.totalReservationsToday})</h6>

                  <Button
                    color="outline-primary"
                    onClick={() => navigate("/reservations")}
                  >
                    {titleCase(t("view all"))}
                  </Button>
                </div>

                <FullCalendar
                  plugins={[timeGridPlugin]}
                  initialView="timeGridDay"
                  height="auto"
                  events={reservationEvents}
                  eventContent={(arg) => {
                    const lines = arg.event.title.split('\n')

                    return (
                      <div>
                        {lines.map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                    )
                  }}
                  slotMinTime={earliestStartTime}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Dashboard;